import React, { useRef } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import Header, { DESKTOP, MOBILE } from '@redbubble/design-system/react/Header';
import * as constants from '@redbubble/design-system/react/constants';
import Box from '@redbubble/design-system/react/Box';
import gql from 'graphql-tag';
import config from '../../../config';
import { useEngageTextMessaging } from './useEngageTextMessaging';
import styles from './Header.scss';
import ProductNav from '../../components/ProductNav';
import { onSearchCallback, onTypaheadResultClick } from '../../lib/gtm/utils';
import {
  trackGA4GlobalNavViewCategory,
  trackGA4GlobalNavViewSubCategory
} from './analytics';
import { getInitialSeconds } from './getCountdownSeconds';

const DEFAULT_LOCALE = config('defaultLocale');

const getPromoBannerHeight = (banners, profile) => {
  if (!banners || banners.length === 0) return 0;
  return profile === constants.DESKTOP ? 48 : 80;
};

export const ExploreHeader = props => {
  const { userInfo, userAccount, browser, banners, intl, redirectUrl } = props;

  const { locale, countryCode, experiments } = userInfo || {};

  const browserProfile = browser && browser.is.large ? DESKTOP : MOBILE;

  const isMobile = browserProfile === MOBILE;

  const engagingPlaceholderMessages = useEngageTextMessaging(intl);
  const headerRef = useRef();

  const promoBannerHeight = getPromoBannerHeight(banners, browserProfile);

  const initialSeconds = getInitialSeconds(banners);

  return (
    <>
      <Box className={styles.header} ref={headerRef}>
        <Header
          {...props}
          searchingExperience={isMobile}
          profile={browserProfile}
          locale={locale}
          countryCode={countryCode}
          audience="all"
          userAccount={userAccount}
          experiments={experiments || []}
          useRainbowStyle
          engagingPlaceholderMessages={engagingPlaceholderMessages}
          showExpandedMobileSearch={isMobile}
          bannerMessages={banners}
          initialSeconds={initialSeconds}
          position="fixed"
          onSearchCallback={onSearchCallback}
          onTypaheadResultClick={onTypaheadResultClick}
          onNavItemClick={(item, rootItem) =>
            trackGA4GlobalNavViewSubCategory(item, {
              position: 'left_side',
              parent: get(rootItem, 'label')
            })
          }
          onNavItemView={item =>
            trackGA4GlobalNavViewCategory(item, {
              position: 'left_side'
            })
          }
          redirectUrl={redirectUrl}
        />

        <div data-testid="header-product-nav">
          <ProductNav
            userInfo={userInfo}
            peeking
            promoBannerHeight={promoBannerHeight}
            onNavItemClick={(item, rootItem) =>
              trackGA4GlobalNavViewSubCategory(item, {
                position: 'header',
                parent: get(rootItem, 'label')
              })
            }
            redirectUrl={redirectUrl}
          />
        </div>
      </Box>
    </>
  );
};

ExploreHeader.displayName = Header.displayName;

ExploreHeader.fragment = gql`
  fragment ${ExploreHeader.displayName} on rbm_Cart {
    id
    itemsCount
  }
`;

ExploreHeader.propTypes = {
  userInfo: PropTypes.shape({
    locale: PropTypes.string,
    countryCode: PropTypes.string
  }),
  userAccount: PropTypes.shape({
    isArtist: PropTypes.bool
  }),
  browser: PropTypes.shape({
    is: PropTypes.shape({
      large: PropTypes.bool
    })
  })
};

ExploreHeader.defaultProps = {
  userInfo: {
    locale: DEFAULT_LOCALE,
    countryCode: 'us'
  },
  userAccount: {
    isArtist: false
  },
  browser: {
    is: {
      large: true
    }
  }
};

export default ExploreHeader;
